import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Modal } from '@/components/synapse/Modal';
import { PaginationFooter } from '@/components/synapse/PaginationFooter';
import { useDebounced } from '@/hooks/useDebounced';
import { usePlatformClinics, useSetClinicDisabled } from '@/hooks/usePlatformAdmin';
import { translateApiError } from '@/lib/apiError';
import type { PlatformClinic } from '@/types/api';

const PAGE_SIZE = 25;

/**
 * Every clinic on the platform, with the switch that turns one off.
 *
 * Disabling cuts off an entire clinic's staff, its QR pages and its patient
 * portal links in one click, so it goes through a confirmation naming the
 * clinic. Enabling does not: restoring service is not a decision anyone needs
 * protecting from.
 */
export function PlatformClinicsPage() {
  const { t } = useTranslation();
  const [rawQuery, setRawQuery] = useState('');
  const [page, setPage] = useState(1);
  const [confirmTarget, setConfirmTarget] = useState<PlatformClinic | null>(null);

  const query = useDebounced(rawQuery.trim(), 250);
  const { data, isLoading, error } = usePlatformClinics({
    query,
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
  });
  const toggle = useSetClinicDisabled();

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  function onSearch(value: string) {
    setRawQuery(value);
    // A filtered list is a different list; staying on page 4 of it would show an
    // empty page most of the time.
    setPage(1);
  }

  return (
    <div className="flex flex-col gap-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-[-0.02em]">
            {t('platformAdmin.clinics.title')}
          </h1>
          <p className="mt-1 text-[13.5px] text-muted">{t('platformAdmin.clinics.subtitle')}</p>
        </div>
        <Input
          value={rawQuery}
          onChange={(e) => onSearch(e.target.value)}
          placeholder={t('platformAdmin.clinics.searchPlaceholder')}
          aria-label={t('platformAdmin.clinics.searchPlaceholder')}
          className="w-full sm:w-72"
        />
      </header>

      {/* Not while the dialog is up: it renders the same error itself, right
          where the operator is looking. */}
      {toggle.isError && !confirmTarget && (
        <div
          role="alert"
          className="rounded-md bg-destructive-bg/40 px-3 py-2 text-[12.5px] text-destructive"
        >
          {translateApiError(toggle.error, { fallback: t('platformAdmin.clinics.toggleFailed') })}
        </div>
      )}

      <Card className="overflow-hidden p-0">
        {isLoading ? (
          <div className="px-5 py-10 text-center text-[13px] text-muted">
            {t('platformAdmin.loading')}
          </div>
        ) : error ? (
          <div className="px-5 py-10 text-center text-[13px] text-destructive">
            {translateApiError(error, { fallback: t('platformAdmin.clinics.loadFailed') })}
          </div>
        ) : items.length === 0 ? (
          <div className="px-5 py-10 text-center text-[13px] text-muted">
            {t('platformAdmin.clinics.empty')}
          </div>
        ) : (
          <ul className="divide-y divide-line">
            {items.map((clinic) => (
              <li
                key={clinic.id}
                className="flex items-center gap-3 px-4 py-3.5 sm:px-5"
                data-disabled={clinic.disabled || undefined}
              >
                {/* The name is the way in. A disabled clinic stays clickable —
                    unlike in the member's own picker, where the destination
                    genuinely does not exist for them: an operator's reason to
                    open it (who is locked out?) is strongest precisely when it
                    is switched off. */}
                <Link
                  to={`/admin/clinics/${clinic.id}`}
                  className={
                    clinic.disabled
                      ? 'min-w-0 flex-1 opacity-60 hover:opacity-80'
                      : 'min-w-0 flex-1'
                  }
                >
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="text-[14px] font-semibold tracking-[-0.01em] hover:underline">
                      {clinic.name}
                    </span>
                    {clinic.disabled && (
                      <Badge tone="danger" size="sm">
                        {t('platformAdmin.clinics.disabledBadge')}
                      </Badge>
                    )}
                  </div>
                  <div className="mt-0.5 truncate text-[12.5px] text-muted">/{clinic.slug}</div>
                </Link>
                <span className="text-[11.5px] tabular-nums text-muted">
                  {t('platformAdmin.clinics.memberCount', { count: clinic.memberCount })}
                </span>
                <Button
                  size="sm"
                  variant={clinic.disabled ? 'secondary' : 'danger'}
                  disabled={toggle.isPending}
                  aria-label={
                    clinic.disabled
                      ? `${t('platformAdmin.clinics.enable')} ${clinic.name}`
                      : `${t('platformAdmin.clinics.disable')} ${clinic.name}`
                  }
                  onClick={() => {
                    // Clear a previous failure first, or the dialog opens with a
                    // stale error about a different clinic already in it.
                    toggle.reset();
                    if (clinic.disabled) {
                      toggle.mutate({ id: clinic.id, disabled: false });
                    } else {
                      setConfirmTarget(clinic);
                    }
                  }}
                >
                  {clinic.disabled
                    ? t('platformAdmin.clinics.enable')
                    : t('platformAdmin.clinics.disable')}
                </Button>
              </li>
            ))}
          </ul>
        )}
        <PaginationFooter
          page={page}
          totalPages={totalPages}
          rangeStart={(page - 1) * PAGE_SIZE + 1}
          rangeEnd={(page - 1) * PAGE_SIZE + items.length}
          total={total}
          onPrev={() => setPage((p) => Math.max(1, p - 1))}
          onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
        />
      </Card>

      {confirmTarget && (
        <DisableClinicDialog
          clinic={confirmTarget}
          toggle={toggle}
          onClose={() => setConfirmTarget(null)}
        />
      )}
    </div>
  );
}

/**
 * The dialog is handed the page's mutation rather than calling the hook itself.
 * Two instances would be two independent states: the page's `isPending` would not
 * cover a disable in flight (so the other rows' Enable buttons would stay live),
 * and the page's error banner would never reflect a failure raised in here.
 */
function DisableClinicDialog({
  clinic,
  toggle,
  onClose,
}: {
  clinic: PlatformClinic;
  toggle: ReturnType<typeof useSetClinicDisabled>;
  onClose: () => void;
}) {
  const { t } = useTranslation();

  const submit = async () => {
    try {
      await toggle.mutateAsync({ id: clinic.id, disabled: true });
      onClose();
    } catch {
      // Inline below — the operator needs to know it did not take effect.
    }
  };

  return (
    <Modal
      title={t('platformAdmin.clinics.confirmTitle', { name: clinic.name })}
      description={t('platformAdmin.clinics.confirmBody', { count: clinic.memberCount })}
      onClose={onClose}
      isPending={toggle.isPending}
      footer={
        <>
          <Button variant="secondary" full onClick={onClose} disabled={toggle.isPending}>
            {t('common.cancel')}
          </Button>
          <Button variant="danger" full onClick={submit} disabled={toggle.isPending}>
            {toggle.isPending ? t('common.saving') : t('platformAdmin.clinics.disable')}
          </Button>
        </>
      }
    >
      {toggle.isError && (
        <div className="rounded-md bg-destructive-bg/40 px-3 py-2 text-[12.5px] text-destructive">
          {translateApiError(toggle.error, { fallback: t('platformAdmin.clinics.toggleFailed') })}
        </div>
      )}
    </Modal>
  );
}
